import React from 'react'
import { Badge } from '@indipia/ui-core'
import type { PartnerCandidate } from '../api/foremanApi'
import { workTypeMatchLabel } from '../constants/foremanPlan'

// 시공파트너 후보 한 줄 — 배정·발송 탭 펼침 목록과 회신 결과 탭 "다른 반장님" 메뉴, 간트 상세창이 함께 쓴다.
// 기획서 C-1-9 후보 행: 1줄 = 이름 + 배지(개인/업체 · 주작업/부작업/주시공지정 · 우선/일반) + 상태 배지,
// 2줄 = ★별점 · 협업 N회 · 일정회신 2시간 내 N% · 보고준수 N% · 하자보고 N% · 활동지역.
// 서버가 내려준 값만 표시한다(값이 null 인 지표는 생략). 내부 id 는 노출하지 않는다.

/** "YYYY-MM-DD" 또는 "YYYY-MM-DDTHH:mm:ss" → "M/D" (시간대 변환 없이 문자열 기준) */
export function formatMonthDay(s: string | null | undefined): string {
  if (!s) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  return m ? `${Number(m[2])}/${Number(m[3])}` : ''
}

/** "YYYY-MM-DDTHH:mm:ss" → "M/D HH:mm" */
export function formatMonthDayTime(s: string | null | undefined): string {
  if (!s) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s)
  return m ? `${Number(m[2])}/${Number(m[3])} ${m[4]}:${m[5]}` : formatMonthDay(s)
}

/** 활동지역 요약 — 시군구명("서울특별시 강남구")에서 시·도만 축약("서울"), 중복 제거. 없으면 '활동지역 미등록'. */
export function regionSummaryText(regions: { code: string; name: string }[] | null | undefined): string {
  if (!regions || regions.length === 0) return '활동지역 미등록'
  const sidos = Array.from(new Set(regions.map(r => {
    const head = (r.name || '').split(' ')[0]
    return head.replace(/(특별자치시|특별자치도|특별시|광역시|도)$/, '')
  }).filter(Boolean)))
  return sidos.join('·') || '활동지역 미등록'
}

/** 일정 겹침 요약 — "{현장명 10자 초과 시 …} M/D~M/D" (첫 겹침 기준, 여러 건이면 " 외 N건") */
export function conflictSummaryText(c: PartnerCandidate): string {
  const list = c.conflicts ?? []
  if (list.length === 0) return ''
  const f = list[0]
  const name = f.siteName ?? ''
  const shortName = name.length > 10 ? `${name.slice(0, 10)}…` : name
  const period = `${formatMonthDay(f.startDate)}~${formatMonthDay(f.endDate)}`
  return `${shortName ? `${shortName} ` : ''}${period}${list.length > 1 ? ` 외 ${list.length - 1}건` : ''}`
}

/** 일정 겹침 확인 문구 — "{이름} 반장님은 같은 날 다른 현장 일정이 있어요 ({현장 M/D}).\n그래도 배정할까요?" */
export function conflictConfirmText(c: PartnerCandidate, action: '배정' | '발송' = '배정'): string {
  return `${c.partnerName} 반장님은 같은 날 다른 현장 일정이 있어요 (${conflictSummaryText(c)}).\n그래도 ${action}할까요?`
}

export interface PartnerCandidateItemProps {
  candidate: PartnerCandidate
  selected?: boolean
  onSelect?: (candidate: PartnerCandidate) => void
  /** 좁은 메뉴용 — 여백·글자 축소 */
  compact?: boolean
}

export function PartnerCandidateItem({ candidate: c, selected = false, onSelect, compact = false }: PartnerCandidateItemProps) {
  const match = workTypeMatchLabel(c.workTypeMatch)

  // 2줄째 지표 — null 은 생략, 협업 0회면 "아직 함께한 현장 없음"
  const metrics: React.ReactNode[] = []
  if (c.collabCount > 0) {
    if (c.avgRating != null) metrics.push(<span key="r"><span style={{ color: 'var(--status-warning-600)' }}>★</span>{c.avgRating.toFixed(1)}</span>)
    metrics.push(<span key="c">협업 {c.collabCount}회</span>)
    if (c.replyWithinTwoHoursPercent != null) metrics.push(<span key="p">일정회신 2시간 내 {c.replyWithinTwoHoursPercent}%</span>)
    if (c.reportComplianceRate != null) metrics.push(<span key="rc">보고준수 {c.reportComplianceRate}%</span>)
    if (c.defectReportRate != null) metrics.push(<span key="d">하자보고 {c.defectReportRate}%</span>)
  } else {
    metrics.push(<span key="n">아직 함께한 현장 없음</span>)
  }
  metrics.push(
    <span key="g" style={c.regionMatch ? undefined : { color: 'var(--status-warning-700)' }} title={c.regionMatch ? undefined : '현장 지역과 활동지역이 달라요'}>
      {regionSummaryText(c.regions)}
    </span>,
  )

  const fs = compact ? 12 : 13
  return (
    <div
      role={onSelect ? 'button' : undefined}
      aria-pressed={onSelect ? selected : undefined}
      onClick={onSelect ? () => onSelect(c) : undefined}
      style={{
        minWidth: 0,
        padding: compact ? '2px 0' : '4px 0',
        opacity: c.scheduleConflict ? 0.55 : 1,
        cursor: onSelect ? 'pointer' : undefined,
        fontWeight: selected ? 600 : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
        <b style={{ fontSize: fs, color: 'var(--shell-on-surface)' }}>{c.partnerName}</b>
        <Badge status="mute">{c.company ? '업체' : '개인'}</Badge>
        {match && <Badge status={c.workTypeMatch === 'SUB' ? 'mute' : 'info'}>{match}</Badge>}
        {c.current && <Badge status="info">배정</Badge>}
        {c.declined && <Badge status="danger">불가 회신{c.declinedDtm ? ` ${formatMonthDay(c.declinedDtm)}` : ''}</Badge>}
        {c.scheduleConflict && <Badge status="warning">일정 겹침 · {conflictSummaryText(c)}</Badge>}
        {c.alreadySent && !c.sendFailed && <Badge status="mute">이미 보냄</Badge>}
        {c.sendFailed && <Badge status="danger">발송 실패</Badge>}
      </div>
      <div style={{ marginTop: 2, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', display: 'flex', flexWrap: 'wrap', gap: '0 6px' }}>
        {metrics.map((m, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span aria-hidden="true">·</span>}
            {m}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
